import { SocialId, SegmentId } from '../constants/socials'
import { EVENT } from '../constants/event'
import { supabase, supabaseEnabled } from './supabase'

export type Lead = {
  id: string
  event: string
  capturedAt: string
  updatedAt: string
  tcgs: string[]
  name: string
  email: string
  phone: string
  facebook: boolean
  socials: Partial<Record<SocialId, string>>
  segments: SegmentId[]
  notes: string
}

// Snake-case wire shape that mirrors the leads table in Supabase.
type LeadRow = {
  id: string
  event_slug: string
  captured_at: string
  updated_at: string
  tcgs: string[]
  name: string
  email: string
  phone: string
  facebook: boolean
  socials: Partial<Record<SocialId, string>>
  segments: SegmentId[]
  notes: string
}

function rowToLead(row: LeadRow): Lead {
  return {
    id: row.id,
    event: row.event_slug,
    capturedAt: row.captured_at,
    updatedAt: row.updated_at,
    tcgs: row.tcgs ?? [],
    name: row.name ?? '',
    email: row.email ?? '',
    phone: row.phone ?? '',
    facebook: !!row.facebook,
    socials: row.socials ?? {},
    segments: row.segments ?? [],
    notes: row.notes ?? '',
  }
}

function leadToRow(lead: Lead): LeadRow {
  return {
    id: lead.id,
    event_slug: lead.event,
    captured_at: lead.capturedAt,
    updated_at: lead.updatedAt,
    tcgs: lead.tcgs,
    name: lead.name,
    email: lead.email,
    phone: lead.phone,
    facebook: lead.facebook,
    socials: lead.socials,
    segments: lead.segments,
    notes: lead.notes,
  }
}

const LEADS_KEY = 'nanza:leads'

function readLocal(): Lead[] {
  try {
    const raw = localStorage.getItem(LEADS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeLocal(leads: Lead[]) {
  localStorage.setItem(LEADS_KEY, JSON.stringify(leads))
}

export function generateLeadId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 10)
}

// Returns the current event's leads, sorted newest first. Pulls from Supabase
// when configured and falls back to localStorage otherwise (the localStorage
// copy is always kept fresh by saveLead so it can serve as an offline cache).
export async function listLeads(): Promise<Lead[]> {
  if (supabaseEnabled && supabase) {
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .eq('event_slug', EVENT.slug)
      .order('captured_at', { ascending: false })
    if (!error && data) {
      const leads = (data as LeadRow[]).map(rowToLead)
      // Refresh the local cache so the offline fallback isn't stale.
      writeLocal(leads)
      return leads
    }
    if (error) console.warn('[leads] supabase list failed', error)
  }
  return readLocal()
    .filter((l) => l.event === EVENT.slug)
    .sort((a, b) => b.capturedAt.localeCompare(a.capturedAt))
}

// Upsert a lead and snapshot the new state into lead_revisions for restore /
// audit. Local cache is always written first so the UI is responsive even
// when Supabase is slow or unreachable.
export async function saveLead(input: Lead): Promise<Lead> {
  const next: Lead = { ...input, updatedAt: new Date().toISOString() }

  const leads = readLocal()
  const idx = leads.findIndex((l) => l.id === input.id)
  if (idx >= 0) leads[idx] = next
  else leads.unshift(next)
  writeLocal(leads)

  if (supabaseEnabled && supabase) {
    const row = leadToRow(next)
    const { error } = await supabase
      .from('leads')
      .upsert(row, { onConflict: 'id' })
    if (error) {
      console.warn('[leads] supabase upsert failed', error)
    } else {
      const { error: revError } = await supabase.from('lead_revisions').insert({
        lead_id: next.id,
        event_slug: next.event,
        snapshot: row,
      })
      if (revError) console.warn('[leads] revision insert failed', revError)
    }
  }
  return next
}
